// src/pages/curso/CursoExtensao.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Button, CircularProgress, Container, Divider, Fade, Paper, Tab, Tabs, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface Turma {
  id: number | string;
  nomeTurma: string;
  nomeUnidadeOperativa?: string;
  formaDeExecucao?: string;
  cargaHoraria?: number;
  informacoesParaVendaOnline?: string;
  informacoesDoPlanoDoCurso?: { objetivos?: string; perfilProfissional?: string; };
  horariosSemAbreviacao?: string[];
  dataPeriodoExecucaoInicialFormatada?: string;
  dataPeriodoExecucaoFinalFormatada?: string;
  recursosFinanceiros?: Array<{ linkMatriculaOnlineDoRecursoFinanceiroNovo?: string }>;
  ofertas?: Array<{ formasDePagamento?: Array<{ disponivelParaWeb?: boolean; meioDePagamentoDescricao?: string; numeroDeParcelas?: number; valorDaParcela?: number; totalPagamento?: number; }> }>;
  linkInteresse?: string;
  linkInscricao?: string;
}
const currencyBRL = (n: number | string | undefined) => Number(typeof n==='string'?Number(n): (n??0)).toLocaleString('pt-BR',{style:'currency',currency:'BRL'});
async function getWithFallback(paths: string[]) { let last:any=null; for (const u of paths){ try{return (await axios.get(u,{timeout:20000})).data;}catch(e){last=e;} } throw last; }

export default function CursoExtensao() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [curso, setCurso] = useState<Turma | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [isEmBreve, setIsEmBreve] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);

  const host = typeof window!=='undefined' ? window.location.hostname : 'localhost';
  const turmasURLs = ['/turmas','/api/turmas',`http://${host}:5000/turmas`,`http://${host}:5000/api/turmas`,'http://localhost:5000/turmas','http://localhost:5000/api/turmas'];

  useEffect(()=>{(async()=>{
    setLoading(true);
    try{
      const data = await getWithFallback(turmasURLs);
      const ativas: Turma[] = data?.turmasAtivas || [];
      const emBreve: Turma[] = data?.turmasEmBreve || [];
      let found = ativas.find(c=>String(c.id)===String(id)) || null;
      let manual=false;
      if(!found){found = emBreve.find(c=>String(c.id)===String(id)) || null; manual=!!found;}
      if(!found) return navigate('/404');
      setCurso(found);
      setIsEmBreve(manual);
    }finally{setLoading(false);}
  })(); // eslint-disable-next-line
  },[id]);

  if(loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh"><CircularProgress/></Box>;
  if(!curso) return <Typography sx={{ textAlign:'center', py:6 }} color="error">Curso não encontrado</Typography>;

  const formas = curso?.ofertas?.[0]?.formasDePagamento || [];
  const linkMatriculaDireta =
    curso?.recursosFinanceiros?.find(r => r.linkMatriculaOnlineDoRecursoFinanceiroNovo)?.linkMatriculaOnlineDoRecursoFinanceiroNovo
    || curso?.linkInscricao
    || (curso as any)?.linkMatriculaOnline
    || null;

  const LINK_DUVIDAS = 'https://forms.gle/H8CzXrfikKinRuHY7';

  const handleTab = (_e:any,v:number)=>{ setTabLoading(true); setTimeout(()=>{setTab(v); setTabLoading(false);},180); };

  return (
    <Container sx={{ py:5 }}>
      <Button variant="contained" startIcon={<ArrowBackIcon/>} onClick={()=>navigate(-1)} sx={{ mb:4, bgcolor:'#2b6cb0', '&:hover':{bgcolor:'#2c5282'} }}>
        Voltar para Cursos
      </Button>

      <Typography variant="h4" fontWeight={600} textAlign="center" gutterBottom>{curso.nomeTurma}</Typography>

      {isEmBreve ? (
        <Paper elevation={2} sx={{ p:4, mb:5 }}>
          <Typography variant="h6" gutterBottom>Sobre o Curso</Typography>
          <Box dangerouslySetInnerHTML={{ __html: curso.informacoesParaVendaOnline || '' }} />
          {curso.informacoesDoPlanoDoCurso?.objetivos && (<><Typography variant="h6" sx={{ mt:3 }}>Objetivos</Typography><Box dangerouslySetInnerHTML={{ __html: curso.informacoesDoPlanoDoCurso.objetivos }} /></>)}
          <Divider sx={{ my:3 }}/>
          <Typography><strong>Carga Horária:</strong>{' '}
            {typeof curso.cargaHoraria === 'number'
              ? `${curso.cargaHoraria} horas`
              : 'A definir'}
          </Typography>
          <Typography><strong>Curso:</strong> {curso.formaDeExecucao || 'A definir'}</Typography>
          {curso.linkInteresse && (
            <Button variant="contained" sx={{ mt:3, backgroundColor:'#E3006D', '&:hover':{ backgroundColor:'#9F004D' } }} href={curso.linkInteresse} target="_blank" rel="noopener noreferrer">
              Registre seu Interesse
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Tabs value={tab} onChange={handleTab} variant="scrollable" scrollButtons="auto" sx={{ mb:4 }}>
            <Tab label="Sobre o Curso" />
            <Tab label="Investimento" />
          </Tabs>

          <Paper elevation={2} sx={{ p:4, mb:5, minHeight:250 }}>
            {tabLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px"><CircularProgress/></Box>
            ) : (
              <Fade in timeout={300}>
                <Box>
                  {tab===0 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>Sobre o Curso</Typography>
                      {curso.informacoesParaVendaOnline && <Box dangerouslySetInnerHTML={{ __html: curso.informacoesParaVendaOnline }} />}
                      {curso.informacoesDoPlanoDoCurso?.objetivos && (<><Typography variant="h6" sx={{ mt:3 }}>Objetivos</Typography><Box dangerouslySetInnerHTML={{ __html: curso.informacoesDoPlanoDoCurso.objetivos }} /></>)}
                    </Box>
                  )}
                  {tab===1 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>Investimento</Typography>
                      <Box sx={{ mb:3 }}>
                        <Typography variant="subtitle1">Opções de Pagamento:</Typography>
                        {formas.length>0 ? formas.map((f,i)=> f?.disponivelParaWeb ? (
                          <Box key={i} sx={{ mb:2 }}>
                            <Typography fontWeight={600}>{f.meioDePagamentoDescricao}</Typography>
                            {Number(f.numeroDeParcelas||0)>1 ? (
                              <>
                                <Typography>Parcelas: {f.numeroDeParcelas}x de {currencyBRL(f.valorDaParcela)}</Typography>
                                <Typography>Total: {currencyBRL(f.totalPagamento)}</Typography>
                              </>
                            ) : (<Typography>À vista: {currencyBRL(f.totalPagamento)}</Typography>)}
                            {i<formas.length-1 && <Divider sx={{ my:2 }}/>}
                          </Box>
                        ):null) : <Typography>Informações de investimento não disponíveis.</Typography>}
                      </Box>

                      <Typography><strong>Carga Horária:</strong>{' '}
                        {typeof curso.cargaHoraria === 'number'
                          ? `${curso.cargaHoraria} horas`
                          : 'A definir'}
                      </Typography>
                      <Typography><strong>Curso:</strong> {curso.formaDeExecucao || 'A definir'}</Typography>

                      {linkMatriculaDireta && (
                        <Button
                          variant="contained"
                          sx={{ mt:3, backgroundColor:'#E3006D', '&:hover':{ backgroundColor:'#9F004D' } }}
                          href={linkMatriculaDireta}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Fazer Matrícula
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </Fade>
            )}
          </Paper>
        </>
      )}

      {/* ——————————————————————————————————————————————
          BLOCO FINAL: Botões (Extensão) — Matrícula + Dúvidas
         —————————————————————————————————————————————— */}
      <Box sx={{ backgroundColor:'#f9fbff', p:4, borderRadius:4, textAlign:'center' }}>
        <Typography variant="h5" gutterBottom>Matrículas abertas para Cursos de Extensão</Typography>
        <Divider sx={{ mb:2 }} />
        <Typography>Garanta sua vaga nos cursos de Extensão do Senac AM.</Typography>
        <Typography sx={{ mt:2 }}>Clique no botão abaixo para se inscrever ou fale conosco em caso de dúvidas.</Typography>

        <Box sx={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:2, mt:3 }}>
          {linkMatriculaDireta && (
            <Button
              variant="contained"
              href={linkMatriculaDireta}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ borderRadius:'24px', backgroundColor:'#E3006D', '&:hover':{ backgroundColor:'#9F004D' } }}
            >
              Fazer Matrícula
            </Button>
          )}

          <Button
            variant="outlined"
            href={LINK_DUVIDAS}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              borderRadius: '24px',
              borderColor: '#E3006D',
              color: '#E3006D',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#9F004D',
                backgroundColor: 'rgba(227,0,109,0.05)',
              },
            }}
          >
            DÚVIDAS NA MATRÍCULA? FALE CONOSCO
          </Button>
        </Box>
      </Box>
    </Container>
  );
}
